import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Users, Zap, MessageSquare, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNetworking } from '@/contexts/NetworkingContext';
import { useMemberGrowth } from '@/hooks/useMemberGrowth';
import { useIntelligentMatchmaking } from '@/hooks/useIntelligentMatchmaking';
import { getDisplayName } from '@/lib/userDisplayUtils';
import { cn } from '@/lib/utils';

const UserNetworkingStats = () => {
  const { user, member } = useAuth();
  const { networkingStats, connections, connectionRequests, messages } = useNetworking();
  const { growth, loading: growthLoading } = useMemberGrowth();
  const { getAnalytics } = useIntelligentMatchmaking({ limit: 10 });
  const matchmakingStats = getAnalytics();

  if (!user || !member) return null;

  const displayName = getDisplayName({
    firstname: member.firstname,
    lastname: member.lastname,
    name: member.name,
    email: member.email
  });

  const activeConnections = connections.length;
  const outgoingRequests = connectionRequests.filter(
    req => req.status === 'pending' && req.requester_id === user.id
  ).length;
  const incomingRequests = connectionRequests.filter(
    req => req.status === 'pending' && req.requested_id === user.id
  ).length;
  const unreadCount = messages.filter(
    msg => !msg.is_read && msg.recipient_id === user.id
  ).length;

  const stats = [
    { icon: Users, label: 'Connexions', value: networkingStats.totalConnections, sub: `${activeConnections} actives`, color: 'text-blue-600' },
    { icon: Zap, label: 'Suggestions', value: matchmakingStats.totalSuggestions, sub: `${Math.round(matchmakingStats.averageCompatibility)}% compat.`, color: 'text-purple-600' },
    { icon: MessageSquare, label: 'Messages', value: networkingStats.totalMessages, sub: `${unreadCount} non lus`, color: 'text-green-600' },
    { icon: Clock, label: 'En attente', value: outgoingRequests, sub: `${outgoingRequests} envoyées`, color: 'text-orange-600', highlight: outgoingRequests > 0 },
  ];

  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-yellow-50 to-yellow-100/50 dark:from-yellow-900/20 dark:to-yellow-800/10 border border-yellow-200/60 dark:border-yellow-800/40">
      <Avatar className="h-10 w-10 shrink-0 ring-2 ring-primary/20">
        <AvatarImage src={member.image || ''} alt={displayName} />
        <AvatarFallback className="bg-primary/10 text-primary text-sm">
          {member.firstname?.[0] || 'U'}{member.lastname?.[0] || ''}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm truncate">{displayName}</span>
          <Badge variant="outline" className="text-[10px] h-5 shrink-0">Actif</Badge>
        </div>
        {!growthLoading && growth?.growthPercentage !== undefined && (
          <span className={cn("text-xs", growth.growthPercentage > 0 ? "text-green-600" : "text-muted-foreground")}>
            {growth.growthPercentage > 0 ? '+' : ''}{growth.growthPercentage.toFixed(1)}% (30j)
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 sm:gap-5">
        {stats.map((s) => (
          <div
            key={s.label}
            className={cn(
              "text-center px-2 py-1 rounded-lg",
              s.highlight && "ring-1 ring-orange-400/60 bg-orange-50 dark:bg-orange-950/30"
            )}
          >
            <div className="flex items-center justify-center gap-1 mb-0.5">
              <s.icon className={cn("h-3 w-3", s.color)} />
              <span className="text-[10px] text-muted-foreground hidden sm:inline">{s.label}</span>
            </div>
            <div className={cn("text-lg font-bold leading-none", s.highlight ? "text-orange-600" : "text-foreground")}>
              {s.value}
            </div>
            <div className="text-[9px] text-muted-foreground mt-0.5 hidden md:block">{s.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserNetworkingStats;
